import { guideEntries } from './guideIndex'
import { documentedAccessLevels, nameKey } from './worldFormat'
import type { DropRoute, DropRule, EventDirectItem, Spawn, WorldData, WorldMap } from '../types/world'

export type PresenceKind = 'spot' | 'invasion' | 'evento'

type Derived = {
  altGroups: Map<string, Spawn[]>
  mapByName: Map<string, WorldMap>
  routesByRule: Map<string, DropRoute[]>
  guideItemByName: Map<string, string>
  monsterKinds: Map<number, Set<PresenceKind>>
  monsterMaps: Map<number, Set<number>>
  mapKinds: Map<number, Set<PresenceKind>>
  mapMonsters: Map<number, Set<number>>
}

const cache = new WeakMap<WorldData, Derived>()

const add = <K, V>(m: Map<K, Set<V>>, k: K, v: V) => {
  const s = m.get(k)
  if (s) s.add(v)
  else m.set(k, new Set([v]))
}

/** Las filas de una aparición aleatoria son alternativas entre sí: no se suman. */
export const altKey = (s: Spawn) =>
  s.mapSelection === 'random' ? `${s.eventId ?? ''}|${s.monsterId}|${s.selectionGroup ?? ''}|${s.possibleMaps.join(',')}` : undefined

export function derived(w: WorldData): Derived {
  const hit = cache.get(w)
  if (hit) return hit
  const altGroups = new Map<string, Spawn[]>()
  w.spawns.forEach((s) => {
    const k = altKey(s)
    if (!k) return
    const arr = altGroups.get(k)
    if (arr) arr.push(s)
    else altGroups.set(k, [s])
  })
  const mapByName = new Map<string, WorldMap>()
  w.maps.forEach((m) => {
    const k = nameKey(m.name)
    if (!mapByName.has(k)) mapByName.set(k, m)
  })
  const routesByRule = new Map<string, DropRoute[]>()
  w.routes.forEach((r) => {
    if (!r.ruleId) return
    const arr = routesByRule.get(r.ruleId)
    if (arr) arr.push(r)
    else routesByRule.set(r.ruleId, [r])
  })
  const guideItemByName = new Map<string, string>()
  guideEntries.filter((e) => e.kind === 'item').forEach((e) => guideItemByName.set(nameKey(e.title), e.id))

  const monsterKinds = new Map<number, Set<PresenceKind>>()
  const monsterMaps = new Map<number, Set<number>>()
  const mapKinds = new Map<number, Set<PresenceKind>>()
  const mapMonsters = new Map<number, Set<number>>()
  w.locations.forEach((l) => {
    const k: PresenceKind = l.kind === 'invasion' ? 'invasion' : l.kind === 'event' ? 'evento' : 'spot'
    add(monsterKinds, l.monsterId, k)
    add(monsterMaps, l.monsterId, l.mapId)
    add(mapKinds, l.mapId, k)
    add(mapMonsters, l.mapId, l.monsterId)
  })
  w.spawns.forEach((s) => {
    const k: PresenceKind = s.kind === 'invasion' ? 'invasion' : 'spot'
    add(monsterKinds, s.monsterId, k)
    add(monsterMaps, s.monsterId, s.mapId)
    add(mapKinds, s.mapId, k)
    add(mapMonsters, s.mapId, s.monsterId)
  })
  const d = { altGroups, mapByName, routesByRule, guideItemByName, monsterKinds, monsterMaps, mapKinds, mapMonsters }
  cache.set(w, d)
  return d
}

export function alternatives(w: WorldData, s: Spawn): Spawn[] {
  const k = altKey(s)
  return k ? derived(w).altGroups.get(k) ?? [s] : [s]
}

export function selectionNote(s: Spawn): string | undefined {
  if (s.mapSelection === 'random') {
    if (s.simultaneous === false) return 'Aparece en uno de los mapas posibles durante la invasión.'
    if (s.simultaneous === true) return 'Aparece en todos los mapas posibles a la vez.'
    return 'El mapa se elige al azar entre los mapas posibles.'
  }
  if (s.mapSelection === 'fixed') return 'Siempre aparece en este mapa.'
  return undefined
}

export type Access = 'yes' | 'no' | 'unknown'

export function mapAccess(m: WorldMap | undefined, level: number | undefined): Access {
  if (level === undefined) return 'yes'
  if (!m) return 'unknown'
  const levels = documentedAccessLevels(m)
  if (!levels.length) return 'unknown'
  return Math.min(...levels) <= level ? 'yes' : 'no'
}

// ── Drops ──

export type MonsterDrop = { rule: DropRule; routes: DropRoute[]; via: 'filtro' | 'objetivo' }

/** Reglas de drop que nombran explícitamente a este monstruo (por filtro o por objetivo). Nunca por nivel o mapa. */
export function monsterDrops(w: WorldData, monsterId: number): { rules: MonsterDrop[]; eventItems: EventDirectItem[] } {
  const d = derived(w)
  const seen = new Set<string>()
  const rules: MonsterDrop[] = []
  ;(w.idx.rulesByMonster.get(monsterId) ?? []).forEach((rule) => {
    seen.add(rule.id)
    rules.push({ rule, routes: d.routesByRule.get(rule.id) ?? [], via: 'filtro' })
  })
  ;(w.idx.targetsByMonster.get(monsterId) ?? []).forEach((t) => {
    if (seen.has(t.ruleId)) return
    const rule = w.rules.get(t.ruleId)
    if (!rule) return
    seen.add(rule.id)
    rules.push({ rule, routes: d.routesByRule.get(rule.id) ?? [], via: 'objetivo' })
  })
  return { rules, eventItems: w.idx.eventItemsByMonster.get(monsterId) ?? [] }
}

/**
 * Rutas de un ítem o variante.
 * - Ítem base ("7,1"): TODAS las rutas con ese item_id, tengan o no variant_id. Antes se descartaban
 *   las rutas con variant_id distinto, y los ítems cuyas rutas llevan variante quedaban "sin rutas".
 * - Variante ("14,23@1"): solo las rutas de esa variante exacta (nunca se mezcla @0 con @1).
 * Los IDs se comparan como texto.
 */
export function itemRoutes(w: WorldData, id: string): DropRoute[] {
  const variant = w.variants.get(id)
  const isChildVariant = !!variant && variant.id !== variant.itemId
  const byVariant = w.idx.routesByVariant.get(id) ?? []
  const byItem = isChildVariant ? [] : w.idx.routesByItem.get(id) ?? []
  const seen = new Set<string>()
  return [...byVariant, ...byItem].filter((r) => (seen.has(r.id) ? false : (seen.add(r.id), true)))
}

export const routeKindLabel: Record<string, string> = {
  rule: 'Drop especial de monstruos',
  natural: 'Drop natural',
  event: 'Premio de evento',
  bag: 'Al abrir una caja o bolsa',
  event_direct: 'Entrega directa en evento',
  quest: 'Misión',
}

/** Monstruos por aparición según spawns.csv, sin sumar alternativas aleatorias. */
export function perAppearance(w: WorldData, eventId: string): { total: number; inconsistent: boolean } {
  const rows = w.spawns.filter((s) => s.eventId === eventId)
  let total = 0
  let inconsistent = false
  const counted = new Set<string>()
  rows.forEach((s) => {
    const k = altKey(s)
    if (!k) {
      total += s.quantity ?? 0
      return
    }
    if (counted.has(k)) return
    counted.add(k)
    const group = derived(w).altGroups.get(k) ?? [s]
    if (new Set(group.map((g) => g.quantity)).size > 1) inconsistent = true
    total += s.quantity ?? 0
  })
  return { total, inconsistent }
}
